import { segment } from '@actions/analytics';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useDoubtDetailsWithoutAuth from '@api/hooks/goal/custom-doubts/useDoubtDetailsWithoutAuth';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeUserId } from '@api/selectors/auth';
import useDoubtDetails from '@api/hooks/goal/custom-doubts/useDoubtDetails';
import useCustomDoubtsStore from '@stores/customDoubtsStore';
import {
  getInstantMatchFlag,
  getInstantMatchFlagSetter,
  getSelectedDoubtSolutionSetter,
  getSubjectSelectionFlow
} from '@stores/selectors/customDoubtSelectors';
import { getSelectedDoubtSolution } from '@api/selectors/doubts';
import useURLs from '@api/hooks/useURLs';
import { useSelector } from 'react-redux';
import { getAccessToken } from '@selectors/auth';
import { getLmpPlayerURL } from '@api/selectors/urls';
import {
  DOW2_INSTANT_MATCH_FAILED,
  DOW2_INVALID_DOUBT,
  DOW2_SOLUTION_SENT,
  DOW2_TIMEOUT
} from '@constants/segment';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import {
  subjectSelectionState,
  VIDEO_RECORDING_STATE
} from '../DoubtSolution/constants';

const MAX_POLLING_COUNT = 3;

const DOUBT_STATUS = {
  SOLUTION_PENDING: 'SOLUTION_PENDING',
  INSTANT_MATCH_IN_PROGRESS: 'INSTANT_SOLUTION_IN_PROGRESS',
  INVALID_DOUBT: 'INVALID_CONTENT',
  TIMEOUT: 'TIMEOUT',
  SOLUTION_AVAILABLE: 'SOLUTION_AVAILABLE',
  SOLVED: 'SOLVED',
  UNDER_EDUCATION_REVIEW: 'UNDER_EDUCATOR_REVIEW',
  NO_ISSUES_FOUND_WHILE_REVIEW: 'NO_ISSUES_FOUND_WHILE_REVIEW',
  ASD_AVAILABLE: 'COMPLETED',
  ASD_FAILED: 'FAILED',
  ASD_PENDING: 'INPROGRESS',
  ASD_NOT_SUPPORTED: 'NOT_SUPPORTED'
};

const useDoubtSolution = ({
  goalUID,
  doubtUID
}: {
  goalUID: string;
  doubtUID: string;
}) => {
  const { doubtDetailsData: doubtDetailsWithoutAuth } =
    useDoubtDetailsWithoutAuth(goalUID, doubtUID);
  const userUID = useMyInfo(getMeUserId);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const isUserSpecificViewAllowed =
    isLoggedIn && userUID === doubtDetailsWithoutAuth.doubtOwnerUID;
  const {
    doubtDetailsData: doubtDetailsWithAuth,
    mutate: revalidateDoubtDetails
  } = useDoubtDetails(goalUID, doubtUID);
  const doubtDetails = doubtDetailsWithAuth;
  const {
    state,
    solutions = [],
    is_sample,
    asdState,
    allowSubjectUpdate,
    subject
  } = doubtDetails;
  const [isSubjectBeingChanged, setIsSubjectBeingChanged] = useState(false);
  const isInstantMatch = useCustomDoubtsStore(getInstantMatchFlag);
  const setInstantMatch = useCustomDoubtsStore(getInstantMatchFlagSetter);
  const { doubtNumber, chats } = useContext(DoubtsAIContext);
  const [doubtSolutionResult, setDoubtSolutionResult] = useState<
    'success' | 'failure' | 'pending'
  >('pending');
  const [doubtQuestionCounter, setDoubtQuestionCounter] = useState(0);
  const setSelectedDoubtSolution = useCustomDoubtsStore(
    getSelectedDoubtSolutionSetter
  );
  const selectedSolutionUID = useCustomDoubtsStore(getSelectedDoubtSolution);
  const selectedSolution = useMemo(
    () => solutions?.find((sol) => sol.uid === selectedSolutionUID) || {},
    [solutions, selectedSolutionUID]
  );

  const timerID = useRef(null);
  const [CURRENT_POLLING_COUNT, SET_CURRENT_POLLING_COUNT] = useState(0);
  const setPolling = () => {
    timerID.current = setInterval(() => {
      revalidateDoubtDetails();
      SET_CURRENT_POLLING_COUNT((prev) => prev + 1);
    }, 5000);
  };

  const objectType =
    selectedSolution?.object_type || selectedSolution?.objectType;

  useEffect(() => {
    if (!asdState) return;
    if (
      (CURRENT_POLLING_COUNT >= MAX_POLLING_COUNT ||
        (asdState !== DOUBT_STATUS.ASD_PENDING &&
          state !== DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS)) &&
      doubtQuestionCounter < 1
    ) {
      clearInterval(timerID.current);
      SET_CURRENT_POLLING_COUNT(0);
      setDoubtSolutionResult('failure');
      if (chats.length < 2) {
        segment.track(DOW2_INSTANT_MATCH_FAILED, {
          doubt_type: 'Doubts AI',
          has_image: 'Yes',
          doubt_id: doubtUID,
          subject: subject?.name,
          ai_doubt_number_of_day: doubtNumber,
          doubt_upload_status: 'Yes'
        });
      }
    }
  }, [
    CURRENT_POLLING_COUNT,
    asdState,
    state,
    allowSubjectUpdate,
    subject?.uid,
    doubtUID,
    subject?.name,
    doubtNumber,
    doubtQuestionCounter,
    chats.length
  ]);

  useEffect(() => {
    const uid = doubtDetails?.solutions?.[0]?.uid ?? null;
    setSelectedDoubtSolution(uid);
  }, [doubtDetails?.solutions, setSelectedDoubtSolution]);

  const doubtKeyObj = {
    [DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS]: 'findingSolution',
    [DOUBT_STATUS.SOLUTION_PENDING]: 'doubtSubmitted',
    [DOUBT_STATUS.INVALID_DOUBT]: 'invalidDoubt',
    [DOUBT_STATUS.TIMEOUT]: 'noSolutionAvailable',
    [DOUBT_STATUS.ASD_AVAILABLE]: 'asdCompleted',
    [DOUBT_STATUS.ASD_FAILED]: 'asdFailed',
    [DOUBT_STATUS.ASD_PENDING]: 'asdPending',
    [DOUBT_STATUS.SOLUTION_AVAILABLE]: 'availableSolution'
  };

  let doubtKey = doubtKeyObj[state];
  if (asdState === DOUBT_STATUS.ASD_PENDING) {
    doubtKey = doubtKeyObj[DOUBT_STATUS.INSTANT_MATCH_IN_PROGRESS];
  }

  const solutionArray = useMemo(
    () => [
      DOUBT_STATUS.SOLUTION_AVAILABLE,
      DOUBT_STATUS.SOLVED,
      DOUBT_STATUS.UNDER_EDUCATION_REVIEW,
      DOUBT_STATUS.NO_ISSUES_FOUND_WHILE_REVIEW
    ],
    []
  );

  useEffect(() => {
    const isSolutionAvailable =
      asdState !== DOUBT_STATUS.ASD_PENDING &&
      (solutionArray.includes(state) || solutions?.length > 0);
    if (isSolutionAvailable) {
      setDoubtQuestionCounter((prev) => prev + 1);
      setDoubtSolutionResult('success');
    }
  }, [asdState, solutionArray, solutions?.length, state]);

  const routeGoalSegmentData = useGoalEventProps({ goalUID });
  const recordingURL = useURLs(getLmpPlayerURL);
  const accessToken = useSelector(getAccessToken);

  const getExtraDataForPlayer = useCallback(
    (selectedUid) => {
      const newSelectedSolution =
        solutions?.find((sol) => sol.uid === selectedUid) || {};
      const { content = {} } = newSelectedSolution?.attachments?.[0] || {};
      const { url } = content;
      if (!url) return {};
      const matchedData = url.match(/&uid=.*/);
      if (!matchedData) return {};
      const uid = matchedData[0].split('_')[1];
      return { state: VIDEO_RECORDING_STATE, uid, video_url: url };
    },
    [solutions]
  );
  const extraLiveClassData = useMemo(
    () => getExtraDataForPlayer(selectedSolutionUID),
    [getExtraDataForPlayer, selectedSolutionUID]
  );

  let liveClassData = {
    isDarkModeOn: true,
    videoSolutionData: {
      access_token: accessToken,
      api_host: process.env.BACKEND_URL.slice(0, -1),
      object_uid: selectedSolutionUID,
      object_type: objectType,
      is_combat: false,
      submit_new_solution: false,
      shouldAutoplay: true,
      is_live_mentoring: false,
      replay: true,
      isLMPReplay: false,
      role: 'listener',
      inlinePlayer: true,
      ...(extraLiveClassData || {})
    }
  };

  const onPlayerReady = () => {};

  const onSolutionClick = (uid) => {
    const extraData = getExtraDataForPlayer(uid);
    liveClassData = {
      isDarkModeOn: true,
      videoSolutionData: {
        access_token: accessToken,
        api_host: process.env.BACKEND_URL.slice(0, -1),
        object_uid: uid,
        object_type: objectType,
        is_combat: false,
        submit_new_solution: false,
        shouldAutoplay: true,
        is_live_mentoring: false,
        replay: true,
        isLMPReplay: false,
        role: 'listener',
        inlinePlayer: true,
        ...(extraData || {})
      }
    };
    (
      document.getElementById('solutionCanvas') as HTMLIFrameElement
    )?.contentWindow?.postMessage(liveClassData, '*');
  };

  const subjectSelectedStateFromStore = useCustomDoubtsStore(
    getSubjectSelectionFlow(goalUID, doubtUID)
  );
  const sendEvent = (eventName, eventProps) => {
    segment.track(eventName, {
      doubt_type: 'Ask A Doubt',
      ...eventProps,
      ...routeGoalSegmentData
    });
  };

  // useEffect for sending event on state change of doubt
  useEffect(() => {
    if (state === DOUBT_STATUS.SOLUTION_AVAILABLE) {
      sendEvent(DOW2_SOLUTION_SENT, {
        is_instant_match: isInstantMatch,
        number_of_solutions: solutions?.length,
        solution_uid: solutions?.map((sol) => sol.uid),
        feature_version: 2,
        doubt_subject_selection:
          subjectSelectedStateFromStore ||
          subjectSelectionState.defaultSubjectSelection,
        flow_type: 'Non_Demo'
      });
      setInstantMatch(false);
    } else if (state === DOUBT_STATUS.INVALID_DOUBT) {
      sendEvent(DOW2_INVALID_DOUBT, {
        doubt_id: doubtUID
      });
    } else if (state === DOUBT_STATUS.TIMEOUT) {
      sendEvent(DOW2_TIMEOUT, {
        doubt_id: doubtUID
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return {
    doubtSolutionResult,
    liveClassData,
    doubtUID,
    goalUID,
    selectedSolutionUID,
    onPlayerReady,
    is_sample,
    recordingURL,
    subject,
    revalidateDoubtDetails,
    isUserSpecificViewAllowed,
    doubtKey,
    doubtDetails,
    onSolutionClick,
    setIsSubjectBeingChanged,
    isSubjectBeingChanged,
    doubtDetailsWithoutAuth,
    solutions,
    setPolling
  };
};

export default useDoubtSolution;
